/** @jsxImportSource @emotion/react */
import type { PropsWithChildren } from 'react';
import React from 'react';
import type { LinkProps } from 'react-router-dom';
import classNames from 'classnames';
import { LoadingOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { Link as ReactRouterLink } from 'react-router-dom';
import { StyleProvider } from '@ant-design/cssinjs';
import {
  ButtonVariant,
  useGetButtonVisualProps,
  type IButtonProps,
} from './button-style-helper';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';

const StyledLink = styled(ReactRouterLink)`
  text-decoration: none;
`;

export const LinkButton: React.FC<
PropsWithChildren<
Omit<IButtonProps, 'type' | 'variant'> &
Partial<Pick<IButtonProps, 'variant'>> &
Omit<LinkProps, 'style' | 'children' | 'color'>
>
> = ({
  children,
  variant = ButtonVariant.FLAT,
  isDisabled,
  loading,
  additionalStyles = {},
  themeToken,
  color,
  inlineButton,
  hideDecorations,

  to,
  size,
  icon,
  iconPosition,
  className,
  dataElementId,
  onClick,
  ...props
}) => {
  const {
    type: visualType,
    color: visualColor,
    variant: visualVariant,
    ghost: visualGhost,
    styles,
    disabled,
  } = useGetButtonVisualProps({
    variant,
    themeToken,
    isDisabled,
    loading,
    color,
    inlineButton,
    hideDecorations,
  });

  const isIconOnly = !React.Children.count(children);

  // classes as in Ant Design Button
  const btnClass = classNames(
    'ant-btn',
    `ant-btn-${visualType}`,
    `ant-btn-color-${visualColor}`,
    `ant-btn-variant-${visualVariant}`,
    className,
    {
      'ant-btn-loading': loading,
      'ant-btn-disabled': disabled,
      'ant-btn-sm': size === 'small',
      'ant-btn-lg': size === 'large',
      'ant-btn-icon-only': isIconOnly,
      'ant-btn-background-ghost': visualGhost,
    },
  );

  // Prepare icon element: loading icon instead usual one when loading
  let iconElement: React.ReactNode = null;
  if (loading) {
    iconElement = (
      <span className="ant-btn-loading-icon">
        <LoadingOutlined />
      </span>
    );
  } else if (icon) {
    iconElement = <span className="ant-btn-icon">{icon}</span>;
  }
  // Prevent click when disabled or loading
  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
  ) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };

  return (
    <StyleProvider hashPriority="high">
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <StyledLink
        to={to}
        aria-disabled={disabled}
        className={btnClass}
        css={[
          styles,
          // Fixes issue where button width fills parent if it's flex:
          { display: 'inline-flex' },
          additionalStyles,
        ]}
        onClick={handleClick}
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: dataElementId,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: dataElementId,
        }}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {(iconPosition === 'start' || !iconPosition) && iconElement}
        {!!children && children}
        {iconPosition === 'end' && iconElement}
      </StyledLink>
    </StyleProvider>
  );
};
